'use client';

import { useSearchParams } from 'next/navigation';

import { useCallback, useMemo, useRef, useState } from 'react';

import { MAP_DEFAULTS } from '@app/consts';
import {
    areTilesEqual,
    BoundingBox,
    isWorldBounds,
    MapTilingHandler,
} from '@app/helpers/map-tiling';

import { useMapDebugOverlay } from './useMapDebugOverlay';

const { MIN_ZOOM, MAX_ZOOM, BRANCHING } = MAP_DEFAULTS;

export const useMapTiles = (map: google.maps.Map | null, isEnabled: boolean) => {
    const searchParams = useSearchParams();
    const isDebugMode = isEnabled && searchParams?.get('debug') === '1';

    const [tiles, setTiles] = useState<BoundingBox[]>([]);
    const [zoom, setZoom] = useState<number | null>(null);

    const tilesRef = useRef<BoundingBox[]>([]);
    tilesRef.current = tiles;

    const tiler = useMemo(() => {
        if (!isEnabled) return null;

        let branching = BRANCHING;

        if (isDebugMode) {
            const branchingParam = searchParams?.get('branching');
            if (branchingParam) {
                const parsed = parseInt(branchingParam, 10);
                if (!isNaN(parsed) && parsed >= 2) {
                    branching = parsed;
                }
            }
        }

        return new MapTilingHandler({
            branching: branching,
            minLevel: MIN_ZOOM,
            maxLevel: MAX_ZOOM,
        });
    }, [isEnabled, isDebugMode, searchParams]);

    // Tiling debug
    const { geoTilesBounds } = useMapDebugOverlay({
        map,
        tiler,
        tiles,
        zoom,
        isDebugMode,
    });

    const getTilesForBounds = useCallback(
        (
            bounds: google.maps.LatLngBoundsLiteral | null,
            zoomLevel: number | null,
        ): BoundingBox[] => {
            if (
                !isEnabled ||
                !tiler ||
                !bounds ||
                zoomLevel === null ||
                zoomLevel < MIN_ZOOM ||
                isWorldBounds(bounds)
            ) {
                setTiles([]);
                setZoom(zoomLevel);
                return [];
            }

            const bbox = new BoundingBox(bounds.south, bounds.west, bounds.north, bounds.east);
            const newTiles = tiler.getTileBoundingBoxesInsideCover(bbox, zoomLevel);

            if (!areTilesEqual(tilesRef.current, newTiles)) {
                setTiles(newTiles);
                setZoom(zoomLevel);

                if (isDebugMode) {
                    console.log('Tiles generated:', {
                        tilesPerAxis: tiler.numberOfTiles(zoomLevel),
                        tileSizeMeter: tiler.tileSizeMetersAt(zoomLevel).toFixed(2),
                        tilesInView: newTiles.length,
                        zoom: zoomLevel,
                        sample: newTiles.slice(0, 10),
                    });
                }

                return newTiles;
            }

            return tilesRef.current;
        },
        [isEnabled, tiler, isDebugMode],
    );

    return { getTilesForBounds, geoTilesBounds };
};
